/**
 * ProjectStatusReportCard — dated status reports on the Performance → Summary
 * tab (DEV-1296 Slice C). Replaces the placeholder narrative.
 *
 * Control standard (mockup B, locked): shared Summary card chrome + "STATUS"
 * header typography (same as Milestones); a bordered date pill + dropdown with
 * bordered ‹ › history nav (mini-project pattern); outlined Edit + subtle
 * Remove; and the filled blue "+ New status" primary button (FP).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { sanitizeRichText } from '../../utils/sanitizeHtml';
import ClampedHtml from '../ClampedHtml/ClampedHtml';
import StatusPhotoStrip, { type StatusPhotoItem } from '../StatusPhotoStrip/StatusPhotoStrip';
import ProjectStatusWriter from '../ProjectStatusWriter/ProjectStatusWriter';
import {
  listProjectStatusReports,
  getProjectStatusReport,
  type ProjectStatusReport,
  type ProjectStatusReportSummary,
} from '../../api/projectStatusReports';
import card from './PerformanceSummary.module.css';
import styles from './ProjectStatusReportCard.module.css';

interface Props {
  projectGuid: string;
  canEdit?: boolean;
}

const ProjectStatusReportCard: React.FC<Props> = ({ projectGuid, canEdit = false }) => {
  const { t } = useAppTranslation('kpi');

  const [list, setList] = useState<ProjectStatusReportSummary[]>([]);
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);
  const [current, setCurrent] = useState<ProjectStatusReport | null>(null);
  const [writerOpen, setWriterOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectStatusReport | null>(null);
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const dateRef = useRef<HTMLDivElement>(null);

  const loadList = useCallback(async () => {
    if (!projectGuid) return;
    try {
      const rows = await listProjectStatusReports(projectGuid);
      setList(rows);
      setSelectedGuid((prev) =>
        prev && rows.some((r) => r.statusReportGuid === prev) ? prev : rows[0]?.statusReportGuid ?? null);
    } catch {
      setList([]);
      setSelectedGuid(null);
    }
  }, [projectGuid]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!projectGuid || !selectedGuid) { setCurrent(null); return; }
    let cancelled = false;
    getProjectStatusReport(projectGuid, selectedGuid)
      .then((r) => { if (!cancelled) setCurrent(r); })
      .catch(() => { if (!cancelled) setCurrent(null); });
    return () => { cancelled = true; };
  }, [projectGuid, selectedGuid]);

  useEffect(() => {
    if (!dateMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) setDateMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [dateMenuOpen]);

  // list is newest-first: idx+1 = older (‹), idx-1 = newer (›).
  const nav = useMemo(() => {
    const idx = list.findIndex((r) => r.statusReportGuid === selectedGuid);
    return {
      olderGuid: idx >= 0 && idx < list.length - 1 ? list[idx + 1].statusReportGuid : null,
      newerGuid: idx > 0 ? list[idx - 1].statusReportGuid : null,
    };
  }, [list, selectedGuid]);

  const fmt = (iso: string) =>
    new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });

  const readPhotos: StatusPhotoItem[] = (current?.photos || []).map((p, i) => ({
    storageKey: p.storageKey, caption: p.caption ?? null, position: p.position ?? i,
  }));

  return (
    <section className={card.card} aria-labelledby="status-report-title">
      <div className={card.cardHeader}>
        <div className={styles.headLeft}>
          <h3 id="status-report-title" className={card.cardTitle}>
            {t('summary.statusReport.cardTitle')}
          </h3>

          {current && (
            <div className={styles.dateNav}>
              <button
                type="button"
                className={styles.navBtn}
                disabled={!nav.olderGuid}
                aria-label={t('summary.statusReport.olderNav')}
                onClick={() => nav.olderGuid && setSelectedGuid(nav.olderGuid)}
              >‹</button>

              <div className={styles.datePillWrap} ref={dateRef}>
                <button
                  type="button"
                  className={styles.datePill}
                  onClick={() => setDateMenuOpen((v) => !v)}
                  disabled={list.length < 2}
                >
                  <span aria-hidden="true">📅</span>
                  {fmt(current.reportingDate)}
                  {list.length > 1 && <span className={styles.caret} aria-hidden="true">▾</span>}
                </button>
                {dateMenuOpen && (
                  <div className={styles.dateMenu}>
                    {list.map((r) => (
                      <button
                        key={r.statusReportGuid}
                        type="button"
                        className={r.statusReportGuid === selectedGuid ? `${styles.dateItem} ${styles.dateItemOn}` : styles.dateItem}
                        onClick={() => { setSelectedGuid(r.statusReportGuid); setDateMenuOpen(false); }}
                      >
                        {fmt(r.reportingDate)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                className={styles.navBtn}
                disabled={!nav.newerGuid}
                aria-label={t('summary.statusReport.newerNav')}
                onClick={() => nav.newerGuid && setSelectedGuid(nav.newerGuid)}
              >›</button>

              {current.postedToJournal && (
                <span className={styles.journalBadge}>📋 {t('summary.statusReport.inJournal')}</span>
              )}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {current && canEdit && (
            <button type="button" className={styles.btnOutline} onClick={() => { setEditing(current); setWriterOpen(true); }}>
              <span aria-hidden="true">✎</span> {t('summary.statusReport.edit')}
            </button>
          )}
          {canEdit && (
            <button type="button" className={styles.btnFilled} onClick={() => { setEditing(null); setWriterOpen(true); }}>
              <span aria-hidden="true">＋</span> {t('summary.statusReport.newButton')}
            </button>
          )}
        </div>
      </div>

      {list.length === 0 ? (
        <p className={styles.empty}>{t('summary.statusReport.empty')}</p>
      ) : current ? (
        <div className={styles.report}>
          <ClampedHtml className={styles.narrative} html={sanitizeRichText(current.narrativeHtml)} />
          {readPhotos.length > 0 && (
            <div className={styles.photos}>
              <StatusPhotoStrip mode="read" photos={readPhotos} />
            </div>
          )}
        </div>
      ) : null}

      {writerOpen && (
        <ProjectStatusWriter
          projectGuid={projectGuid}
          editing={editing}
          onClose={() => setWriterOpen(false)}
          onSaved={() => { setWriterOpen(false); loadList(); }}
        />
      )}
    </section>
  );
};

export default ProjectStatusReportCard;
